import React, { useState } from 'react';
import { cn } from '@/utils/cn';

interface FlowerButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'onToggle'> {
  size?: number;
  onToggle?: (isOpen: boolean) => void;
  defaultOpen?: boolean;
  isOpen?: boolean; // Controlled mode - when provided, component is controlled
  showCenterDot?: boolean;
  centerX?: number;
  centerY?: number;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export const FlowerButton: React.FC<FlowerButtonProps> = ({
  size = 64,
  onToggle,
  defaultOpen = false,
  isOpen: controlledIsOpen,
  showCenterDot = true,
  centerX = 240.5,
  centerY = 396.5,
  className,
  ...props
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

  // Use controlled value if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const newState = !isOpen;
    // Only update internal state if not controlled
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(newState);
    }
    onToggle?.(newState);
    props.onClick?.(e);
  };

  return (
    <button
      type="button"
      className={cn(
        'group relative inline-flex items-center justify-center',
        'transition-transform duration-300 ease-out',
        'hover:scale-105 active:scale-95',
        'rounded-full focus:outline-none focus:ring-2 focus:ring-offset-4 focus:ring-blue-500',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
      style={{ width: size, height: size }}
      onClick={handleClick}
      aria-label={isOpen ? 'Close flower' : 'Open flower'}
      aria-pressed={isOpen}
      {...props}
    >
      <svg
        className="absolute inset-0"
        viewBox="0 0 637.48 637.65"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        {/* Light grey circle background - permanent, sits behind everything */}
        <circle
          cx="318.74"
          cy="318.82"
          r="400"
          className="transition-all duration-200 fill-gray-100 group-hover:fill-primary-100"
        />

        {/* GREY BACKGROUND FLOWER - Always visible, sits behind everything */}
        <g>
          {/* Grey big petal */}
          <path
            fill="#d1d5db"
            d="M452.13,0h-.31c-102.67,0-185.9,83.23-185.9,185.9v169.54c0,8.9,7.22,16.12,16.12,16.12h170.09c102.36,0,185.34-82.98,185.34-185.34v-.87C637.48,82.98,554.49,0,452.13,0ZM490.17,234.49c0,45.74-37.08,82.82-82.82,82.82h-71.99c-6.12,0-11.07-4.96-11.07-11.07v-71.74c0-45.88,37.19-83.06,83.06-83.06h0c45.74,0,82.82,37.08,82.82,82.82v.25Z"
          />
          {/* Grey small petal - upper left */}
          <path
            fill="#d1d5db"
            d="M215.11,356.47v-92.47c0-59.4-48.15-107.55-107.55-107.55h0C48.15,156.45,0,204.6,0,264h0c0,59.4,48.15,107.55,107.55,107.55h92.47c8.33,0,15.08-6.75,15.08-15.08Z"
          />
          {/* Grey small petal - lower right */}
          <path
            fill="#d1d5db"
            d="M265.92,438.11v91.98c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55h0c0-59.4-48.15-107.55-107.55-107.55h-91.98c-8.6,0-15.57,6.97-15.57,15.57Z"
          />
          {/* Grey small petal - lower left */}
          <path
            fill="#d1d5db"
            d="M199.53,422.54h-91.98c-59.4,0-107.55,48.15-107.55,107.55h0c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55v-91.98c0-8.6-6.97-15.57-15.57-15.57Z"
          />
        </g>

        {/* BIG PETAL - Top-right petal (scales from small to large, 1.5x slower) */}
        <path
          className="ease-out"
          style={{
            transformOrigin: '282px 355.56px',
            transform: isOpen ? 'scale(1)' : 'scale(0.5)',
            fill: '#ee3962',
            opacity: isOpen ? 1 : 0,
            transition: isOpen
              ? 'transform 750ms ease-out, opacity 750ms ease-out' // Opening: scale + fade in
              : 'opacity 500ms ease-out', // Closing: fade out to reveal grey behind
          }}
          d="M452.13,0h-.31c-102.67,0-185.9,83.23-185.9,185.9v169.54c0,8.9,7.22,16.12,16.12,16.12h170.09c102.36,0,185.34-82.98,185.34-185.34v-.87C637.48,82.98,554.49,0,452.13,0ZM490.17,234.49c0,45.74-37.08,82.82-82.82,82.82h-71.99c-6.12,0-11.07-4.96-11.07-11.07v-71.74c0-45.88,37.19-83.06,83.06-83.06h0c45.74,0,82.82,37.08,82.82,82.82v.25Z"
        />

        {/* SMALL PETAL - Upper left (rotates counter-clockwise, maintains size) */}
        <path
          className="ease-out"
          style={{
            transformOrigin: `${centerX}px ${centerY}px`,
            transform: isOpen ? 'rotate(0deg)' : 'rotate(90deg)',
            fill: '#ee3962',
            opacity: isOpen ? 1 : 0,
            transition: isOpen
              ? 'transform 500ms ease-out, opacity 500ms ease-out' // Opening: rotate + fade in
              : 'opacity 500ms ease-out', // Closing: fade out to reveal grey behind
          }}
          d="M215.11,356.47v-92.47c0-59.4-48.15-107.55-107.55-107.55h0C48.15,156.45,0,204.6,0,264h0c0,59.4,48.15,107.55,107.55,107.55h92.47c8.33,0,15.08-6.75,15.08-15.08Z"
        />

        {/* SMALL PETAL - Lower right (rotates clockwise, maintains size) */}
        <path
          className="ease-out"
          style={{
            transformOrigin: `${centerX}px ${centerY}px`,
            transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
            fill: '#ee3962',
            opacity: isOpen ? 1 : 0,
            transition: isOpen
              ? 'transform 500ms ease-out, opacity 500ms ease-out' // Opening: rotate + fade in
              : 'opacity 500ms ease-out', // Closing: fade out to reveal grey behind
          }}
          d="M265.92,438.11v91.98c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55h0c0-59.4-48.15-107.55-107.55-107.55h-91.98c-8.6,0-15.57,6.97-15.57,15.57Z"
        />

        {/* SMALL PETAL - Lower left (rotates counter-clockwise 2x speed, maintains size) */}
        <path
          className="ease-out"
          style={{
            transformOrigin: `${centerX}px ${centerY}px`,
            transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)',
            fill: '#ee3962',
            opacity: isOpen ? 1 : 0,
            transition: isOpen
              ? 'transform 500ms ease-out, opacity 500ms ease-out' // Opening: rotate + fade in
              : 'opacity 500ms ease-out', // Closing: fade out to reveal grey behind
          }}
          d="M199.53,422.54h-91.98c-59.4,0-107.55,48.15-107.55,107.55h0c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55v-91.98c0-8.6-6.97-15.57-15.57-15.57Z"
        />

        {/* Center circle - reference point */}
        <circle
          cx={centerX}
          cy={centerY}
          r="40"
          fill="#ee3962"
          opacity={showCenterDot ? 1 : 0}
          className="transition-all duration-500"
        />

        {/* GHOST PETALS - Light grey guides showing final open position (in front when closed, fade to background when open) */}
        <g
          style={{
            opacity: isOpen ? 0 : 1.0,
            transition: 'opacity 500ms ease-out',
          }}
        >
          {/* Ghost - Big petal outer shape */}
          <path
            fill="#d1d5db"
            d="M452.13,0h-.31c-102.67,0-185.9,83.23-185.9,185.9v169.54c0,8.9,7.22,16.12,16.12,16.12h170.09c102.36,0,185.34-82.98,185.34-185.34v-.87C637.48,82.98,554.49,0,452.13,0Z"
          />
          {/* Ghost - Big petal white center */}
          <path
            className="transition-all duration-200 fill-white group-hover:fill-primary-100"
            d="M490.17,234.49c0,45.74-37.08,82.82-82.82,82.82h-71.99c-6.12,0-11.07-4.96-11.07-11.07v-71.74c0-45.88,37.19-83.06,83.06-83.06h0c45.74,0,82.82,37.08,82.82,82.82v.25Z"
          />
          {/* Ghost - Upper left petal */}
          <path
            fill="#d1d5db"
            d="M215.11,356.47v-92.47c0-59.4-48.15-107.55-107.55-107.55h0C48.15,156.45,0,204.6,0,264h0c0,59.4,48.15,107.55,107.55,107.55h92.47c8.33,0,15.08-6.75,15.08-15.08Z"
          />
          {/* Ghost - Lower right petal */}
          <path
            fill="#d1d5db"
            d="M265.92,438.11v91.98c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55h0c0-59.4-48.15-107.55-107.55-107.55h-91.98c-8.6,0-15.57,6.97-15.57,15.57Z"
          />
          {/* Ghost - Lower left petal */}
          <path
            fill="#d1d5db"
            d="M199.53,422.54h-91.98c-59.4,0-107.55,48.15-107.55,107.55h0c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55v-91.98c0-8.6-6.97-15.57-15.57-15.57Z"
          />
        </g>
      </svg>
    </button>
  );
};
